// Kapcsolattartók — az ügyfél-kapcsolattartók (partner identity, `kind='contact'` account-affiliation) címtára
// az ÉLŐ adatból, a KÖZÖS DataTable-re építve (a clients.index / employees a referencia-minta). Szerver-oldali
// kereső (név/e-mail, 300 ms debounce), szerver-rendezés (név, magyar collation — csak az irány váltható),
// végtelen görgetés (100/lap; a lista áttekinthető méretű → gyakorlatban egy lap). A /contacts BFF-végpontról
// fogyaszt (identity + fiók-kötődés-dúsítás, identityre aggregálva).
//
// Legacy-parity (régi CRM „Kapcsolattartók" lista): a MEGJELENÍTHETŐ oszlopok az elérhető foundation-/busz-adatból —
// Kapcsolattartó (profilkép + név + Elsődleges-jelző) · Elérhetőségek · Fiók(ok) · Ügyféladminisztrátorok · Státusz.
// Write-vertikum (a munkatárs-oldal mintája): create/edit a beúszó ContactFormSheet-ben (contacts.manage),
// törlés ConfirmDialog + optimista lista-sor eltávolítás (contacts.delete) — a sor-akciók + a „Új kapcsolattartó"
// gomb jog-kapuzva.
import { type ContactRowSchema } from '@atlas/contracts';
import { ArrowDownTrayIcon, ArrowUturnLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { ContactFormSheet } from '@/components/contacts/contact-form-sheet';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { IdentityStatusBadge } from '@/components/shared/identity-status-badge';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListExportButton } from '@/components/shared/list-export-button';
import { ListNewButton } from '@/components/shared/list-new-button';
import {
  DeleteRowAction,
  EditRowAction,
  RowActionButton,
  RowActions,
} from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonCell } from '@/components/shared/person-cell';
import { PersonRefCell } from '@/components/shared/person-ref-cell';
import { contactKeys } from '@/features/contacts/api';
import { ImpersonateRowAction } from '@/features/impersonation/components/impersonate-row-action';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { useListExport } from '@/hooks/use-list-export';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// Deep-link: `?edit=<identityId>` → a szerkesztő-sheet a megadott kontaktra nyílik (a command palette „2. rungja").
const contactsSearchSchema = z.object({
  edit: z.string().optional(),
  q: z.string().optional(),
  order: z.string().optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute('/contacts')({
  validateSearch: (search) => contactsSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: ContactsRoute,
});

// Route-wrapper: a search-paramot (deep-link) itt olvassuk + a param-törlést itt navigáljuk, a lista-oldal maga
// router-hook-mentes marad (közvetlenül tesztelhető). A záráskor a `?edit` törlődik (replace) → back-barát + újra-nyitható.
function ContactsRoute(): JSX.Element {
  const { edit } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/contacts',
      search: (prev) => ({ ...prev, edit: undefined }),
      replace: true,
    });
  }, [navigate]);
  return <ContactsPage deepLinkEditId={edit} onClearDeepLink={clearDeepLink} />;
}

type ContactRow = z.infer<typeof ContactRowSchema>;

export interface ContactsPageProps {
  /** Deep-link: a `?edit=` paraméterből érkező kontakt-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

const PAGE_SIZE = 100;
/** A státusz-facet „mind" pszeudó-értéke (mint a Munkatársak/Fiókok oldalon). */
const ALL = '__all__';
/** Identity-státusz értékkészlet (a facet-opciókhoz — fix, mint az accounts-nál). */
// ÉLETCIKLUS-OPCIÓK — KÉT elérhető állapot.
//
// ⚠️ A `suspended` SZÁNDÉKOSAN kimaradt: élesben mérve NULLA ilyen sor van, és nincs is felület, ahol
// beállítható lenne (sem a kontakt-, sem a munkatárs-írás body-jában nincs `status`). Opcióként kínálni
// hamis affordancia: a felhasználó rászűrne, és mindig üres listát kapna. Ha a felfüggesztés mint MŰVELET
// kell, az külön feature — a szűrő akkor bővül, amikor az állapot tényleg előállítható.
const STATUS_OPTIONS = ['active', 'archived'] as const;

export function ContactsPage({
  deepLinkEditId,
  onClearDeepLink,
}: ContactsPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('contacts.view');
  // Jog-vezérelt akciók: create/edit → `contacts.manage`, törlés → `contacts.delete`.
  const canManage = can('contacts.manage');
  const canDelete = can('contacts.delete');

  // Szerkesztő-sheet: `null` → zárt; egyébként a szerkesztendő kontakt (előtöltés). Create-sheet + törlés-cél külön.
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ContactRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactRow | null>(null);
  /** A köszöntő-akció megerősítése (valódi levél megy egy külsős postafiókba → megerősítést kér). */
  const [welcomeTarget, setWelcomeTarget] = useState<ContactRow | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<ContactRow | null>(null);

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    order: 'asc',
    // ÉLETCIKLUS — az ALAP az ÉLŐ kapcsolattartó. (Korábban „mind" volt, a szűrő pedig a nyers
    // identity-státusz-enumot kínálta, amiből kettő SOHA nem talált semmit.)
    status: 'active',
  });
  const q = urlFilters.q;
  const order = urlFilters.order as 'asc' | 'desc';
  const statusFilter = urlFilters.status;
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // A rendezés-kulcs fixen a név — de LOCALE-tudatos (HU→vezetéknév, EN→keresztnév); csak az irány váltható.

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: contacts.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // A rendezés + a nevek LOCALE-tudatosak → a nyelv a query-kulcs része (nyelvváltáskor újratölt).
  const lang: 'hu' | 'en' = i18n.language === 'en' ? 'en' : 'hu';
  const filters = { q, order, lang, statusFilter };
  const listKey = contactKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.contacts.query({
        query: {
          q: q || undefined,
          order,
          lang,
          status:
            statusFilter === 'archived' ? 'archived' : statusFilter === 'all' ? 'all' : 'active',
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.contacts : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` kontaktra nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<ContactRow>({
    id: deepLinkEditId,
    rows,
    getId: (c) => c.id,
    onOpen: (c) => setEditTarget(c),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A query nem dob non-2xx-re, csak státuszt ad → a lapok státuszából olvassuk ki a hiba-állapotot.
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  // 401/403 → jog-hiba (általános „nincs jogod" üzenet); minden más hiba → általános betöltési hiba + retry.
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  // az alap `active` is szűkítés (Andi #95) — a „mind" az egyetlen szűretlen állapot
  const hasFilters = q !== '' || statusFilter !== 'all';

  // Egyetlen rendezhető oszlop (név): a fejléc-kattintás CSAK az irányt váltja (a kulcs fix `name`).
  const toggleOrder = useCallback(
    () => setFilters({ order: order === 'asc' ? 'desc' : 'asc' }),
    [order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;

  // Törlés = a kontakt globális offboardingja (optimista lista-sor eltávolítás + rollback, a munkatárs-minta).
  // A BFF a DELETE /contacts/:id-t hívja; siker → a sor eltűnik, hiba → visszagördül.
  type ListPage = Awaited<ReturnType<typeof api.contacts.query>>;
  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.contactDelete.mutation({ params: { id } }),
    invalidateKeys: [contactKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        const id = deleteTarget?.id;
        if (!old || !id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    contacts: p.body.contacts.filter((c) => c.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:contacts.deleted'),
      conflict: t('toast:contacts.deleteError'),
      forbidden: t('toast:contacts.forbidden'),
      validation: t('toast:contacts.validation'),
      error: t('toast:contacts.deleteError'),
    },
  });
  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
    }
  }, [deleteTarget, removeMutation]);

  // VISSZAVÉTEL (re-hire) — a kivezetés tükörképe. Optimista sor-eltávolítás, mint a kivezetésnél: a
  // visszavett személy ÉLŐ lesz, tehát a KIVEZETETT nézetből kikerül (ahol épp áll a felhasználó).
  const restoreMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.contactRestore.mutation({ params: { id } }),
    invalidateKeys: [contactKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        const id = restoreTarget?.id;
        if (!old || !id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    contacts: p.body.contacts.filter((c) => c.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:contacts.restored'),
      // 409 = a sor már él (elavult lista) — SAJÁT üzenet: nem hiba, hanem „töltsd újra".
      conflict: t('toast:contacts.restoreConflict'),
      forbidden: t('toast:contacts.forbidden'),
      validation: t('toast:contacts.validation'),
      error: t('toast:contacts.restoreError'),
    },
  });
  const confirmRestore = useCallback(() => {
    if (restoreTarget) {
      restoreMutation.mutate({ id: restoreTarget.id }, { onSettled: () => setRestoreTarget(null) });
    }
  }, [restoreTarget, restoreMutation]);

  /**
   * ÜDVÖZLŐ LEVÉL (a legacy manuális welcome-gombja, partner-oldal). NEM `useEntityMutation`: nincs optimista
   * lista-írás (a levél nem módosít sort) és nincs 409/422 ág — sima mutáció + toast a helyes alak. A `202`
   * = „elindítottuk" (a kézbesítés fire-and-forget).
   */
  const queryClient = useQueryClient();
  const welcomeMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => api.contactSendWelcome.mutation({ params: { id } }),
    onSuccess: (res) => {
      // A ts-rest a 4xx-et NEM dobja → itt bontunk státusz szerint. A jog-nevet SOHA nem mutatjuk.
      if (res.status === 202) {
        toast.success(t('toast:contacts.welcomeSent'));
        // a levél EGYSZERI: a sor `welcomeAvailable`-je a frissítés után hamis → a gomb eltűnik
        void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      }
      // 409 = a cél NEM kaphat meghívót (az Odin `issued: false` ága: nincs aktív app-tagsága, inaktív a fiókja,
      // governance-admin vagy service-fiók). Ez NEM néma siker: kiírjuk, hogy az admin tudjon mit tenni.
      else if (res.status === 409) toast.error(t('toast:contacts.welcomeNotEligible'));
      else if (res.status === 403) toast.error(t('toast:contacts.forbidden'));
      else toast.error(t('toast:contacts.welcomeError'));
    },
    onError: () => toast.error(t('toast:contacts.welcomeError')),
  });
  const confirmWelcome = useCallback(() => {
    if (welcomeTarget) {
      welcomeMutation.mutate({ id: welcomeTarget.id }, { onSettled: () => setWelcomeTarget(null) });
    }
  }, [welcomeTarget, welcomeMutation]);

  // Sor-akciók: ceruza → szerkesztés (contacts.manage), kuka → törlés (contacts.delete). A create a lap-szintű
  // ListNewButton.
  const rowActions = (c: ContactRow): JSX.Element => {
    // KIVEZETETT SOR: a műveletek az ÉLETCIKLUSRA kapuznak (nem a `status` enumra). A kivezetett
    // kapcsolattartónak nincs élő fiók-kötődése és portál-hozzáférése, tehát a szerkesztő és a meghívó
    // értelmetlen — helyettük EGY művelet marad: a visszavétel.
    //
    // `!= null` (LAZA) SZÁNDÉKOSAN: a hiányzó mező (régi cache, teszt-fixture) `undefined` → ÉLŐ. A szigorú
    // `!== null` minden élő soron eltüntetné a műveleteket.
    const offboarded = c.offboardedAt != null;
    if (offboarded) {
      return (
        <RowActions>
          {canManage && (
            <RowActionButton
              label={t('contacts.restore')}
              icon={ArrowUturnLeftIcon}
              onClick={() => setRestoreTarget(c)}
            />
          )}
        </RowActions>
      );
    }
    return (
      <RowActions>
        {canManage && <EditRowAction label={t('contacts.edit')} onClick={() => setEditTarget(c)} />}
        {/* ÜDVÖZLŐ LEVÉL (user-döntés): a gomb CSAK ott van, ahol a SZERVER kínálhatónak mondja — legalább egy
            fiókján VALÓDI portál-hozzáférés, és még nem kapott meghívót (a levél egyszeri). Nem tiltott gomb
            tooltippel, hanem hiányzó: a lista így azt mutatja, kinek kell még kiküldeni. A `welcomeNotEligible`
            toast a ritka futásidejű 409-re marad (pl. közben megszűnt hozzáférés). */}
        {canManage && c.welcomeAvailable && (
          <RowActionButton
            // lejárt meghívó után a gomb ÚJRAKÜLDÉS (a szerver csak lejárt linknél kínálja újra)
            label={c.invitedAt ? t('contacts.welcomeResend') : t('contacts.welcome')}
            icon={EnvelopeIcon}
            onClick={() => setWelcomeTarget(c)}
          />
        )}
        {/* MEGSZEMÉLYESÍTÉS: „belépés a nevében" — a PARTNER-PORTÁL támogatásának fő esete (a partner
          kapcsolattartó tokenje `type=partner`, tehát a portál-nézet töltődik be). A gomb maga dönti el, hogy
          megjelenik-e (feature-jog + `iam:impersonate` platform-scope). */}
        <ImpersonateRowAction
          identityId={c.id}
          name={formatName(
            {
              givenName: c.givenName,
              familyName: c.familyName,
              displayName: c.displayName ?? c.email,
            },
            i18n.language,
          )}
        />
        {canDelete && (
          <DeleteRowAction label={t('contacts.delete')} onClick={() => setDeleteTarget(c)} />
        )}
      </RowActions>
    );
  };

  // EXPORT: a sorokat a lista-végpont adja, az ÉPPEN AKTÍV szűrőkkel (q + státusz + rendezés), 200-as
  // lapokkal a végéig — tehát a fájl a TELJES szűrt találatot tartalmazza, nem a betöltött lapot.
  const fetchExportPage = useCallback(
    async (offset: number | undefined) => {
      const r = await api.contacts.query({
        query: {
          q: q || undefined,
          order,
          lang,
          status:
            statusFilter === 'archived' ? 'archived' : statusFilter === 'all' ? 'all' : 'active',
          limit: 200,
          offset: offset ?? 0,
        },
      });
      if (r.status !== 200) return null;
      return { rows: r.body.contacts, next: r.body.nextOffset };
    },
    [q, order, lang, statusFilter],
  );

  const columns: DataTableColumn<ContactRow>[] = [
    {
      // 1. Kapcsolattartó — profilkép + név (rendezhető), alatta a BEOSZTÁS (halvány `tertiary`; nincs → „—").
      //    Az e-mail+telefon a külön „Elérhetőségek" oszlopba került; az Elsődleges/Portál badge kivezetve.
      id: 'name',
      header: t('contacts.columns.contact'),
      sortable: true,
      cardLabel: t('contacts.columns.contact'),
      width: 'minmax(0,1.9fr)',
      multiline: true,
      // Az EXPORT a nevet a MEGJELENÍTETTEL egyező locale-sorrendben adja (HU: vezetéknév elöl) — a beosztás
      // külön oszlop lesz, mert egy táblázat-cellában két adat összefűzve nem szűrhető/rendezhető.
      exportValue: (c) =>
        formatName(
          {
            givenName: c.givenName,
            familyName: c.familyName,
            displayName: c.displayName ?? c.email,
          },
          i18n.language,
        ),
      cell: (c) => (
        <PersonCell
          givenName={c.givenName}
          familyName={c.familyName}
          name={c.displayName ?? c.email}
          avatarUrl={c.avatarUrl}
          secondary={c.position ?? emDash}
          badge={
            // Elsődleges kapcsolattartó legalább egy fiókon (a ContactRow.isPrimary aggregátum).
            c.isPrimary ? (
              <DomainBadge
                kind="marker"
                value="primary"
                label={t('contacts.primaryBadge')}
                className="shrink-0"
              />
            ) : null
          }
        />
      ),
    },
    {
      // 2. Elérhetőségek — e-mail + telefon (a fiók-nevek stílusában: a cella alap muted-foreground-ja, NEM tertiary).
      id: 'contactInfo',
      card: 'subtitle',
      header: t('contacts.columns.contactInfo'),
      cardLabel: t('contacts.columns.contactInfo'),
      width: 'minmax(0,1.4fr)',
      multiline: true,
      // A cella két adatot mutat (e-mail + telefon) → az exportban is együtt, de SORTÖRÉSSEL: a CSV/XLSX
      // író a mezőn belüli sortörést szabályosan idézőjelezi, tehát nem csúszik el az oszlop.
      exportValue: (c) => [c.email, c.phone].filter(Boolean).join('\n'),
      cell: (c) => (
        <div className="min-w-0 space-y-0.5">
          <div className="truncate">{c.email ?? emDash}</div>
          {c.phone && <div className="truncate">{c.phone}</div>}
        </div>
      ),
    },
    {
      // 3. Fiók(ok) — a kontakt fiók-kötődései: CSAK a fiók-nevek. ≤3 statikus; >3 legördülő (ExpandableCell);
      //    a név a cella muted-stílusát örökli (2. oszlop).
      // A korábbi per-fiók SZEREPKÖR-ikon KIKERÜLT: a `level` a BFF-ben hardcode-olt `admin`
      //    placeholder volt (a legacy csak `contact`-ot ismert), tehát MINDEN soron ugyanazt az ikont mutatta —
      //    zajos és félrevezető. A tényleges fiókonkénti szerep a fiók oldalán él és ott állítható
      //    (`portal-access`), nem ezen a listán.
      id: 'accounts',
      card: 'subtitle',
      header: t('contacts.columns.accounts'),
      cardLabel: t('contacts.columns.accounts'),
      width: 'minmax(0,1.7fr)',
      multiline: true,
      // A legördülő MÖGÖTTI fiókok is bekerülnek: az export a TELJES listát adja, nem a látszó kettőt.
      exportValue: (c) => c.affiliations.map((a) => a.accountName ?? a.accountId).join('\n'),
      cell: (c) =>
        c.affiliations.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell
            items={c.affiliations}
            getKey={(a) => a.accountId}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('contacts.more', { count: n })}
            triggerAriaLabel={t('contacts.moreItems')}
            renderItem={(a) => <span className="min-w-0 truncate">{a.accountName ?? '—'}</span>}
          />
        ),
    },
    {
      // 4. ÜGYFÉLADMINISZTRÁTOROK — a legacy lista 4. oszlopa: a kontakt fiókjain KERESZTÜL az a belső kolléga,
      //    aki nálunk kezeli. A BFF identityre EGYEDIESÍTVE adja (egy admin több közös fiókon is ülhet), névre
      //    rendezve; a cella a KÖZÖS `PersonRefCell` (avatar + locale-tudatos név, 2 látszik, a többi legördül).
      id: 'admins',
      card: 'subtitle',
      header: t('contacts.columns.clientAdmins'),
      cardLabel: t('contacts.columns.clientAdmins'),
      // A fejléc a leghosszabb ezen a listán („Ügyféladminisztrátorok") → a legszélesebb tartalmi oszlop,
      // hogy NE csonkoljon (mérve: 1.5fr-en „ÜGYFÉLADMINISZTRÁTO…" lett).
      width: 'minmax(0,2.1fr)',
      multiline: true,
      exportValue: (c) =>
        c.admins
          .map((a) =>
            formatName(
              { givenName: a.givenName, familyName: a.familyName, displayName: a.name },
              i18n.language,
            ),
          )
          .join('\n'),
      cell: (c) => (
        <PersonRefCell
          items={c.admins}
          moreLabel={(n) => t('contacts.more', { count: n })}
          triggerAriaLabel={t('contacts.moreItems')}
        />
      ),
    },
    {
      // 5. Státusz — dedikált UTOLSÓ oszlop, pill-badge (mint a Munkatársak-listán; identity-státusz badge).
      //    `multiline` → overflow-hidden (nem truncate) → keskenyítéskor nincs „…" a pill után.
      id: 'status',
      header: t('contacts.columns.status'),
      cardLabel: t('contacts.columns.status'),
      width: 'minmax(0,0.9fr)',
      multiline: true,
      // A státusz a LÁTOTT (lokalizált) címkével megy ki, nem a nyers kulccsal — az export olvasónak készül.
      exportValue: (c) => t(`contacts.statusLabel.${c.status}`, { defaultValue: c.status }),
      cell: (c) => (
        <IdentityStatusBadge
          status={c.status}
          label={t(`contacts.statusLabel.${c.status}`, { defaultValue: c.status })}
        />
      ),
    },
  ];

  const exportCtl = useListExport<ContactRow, number>({
    columns,
    slug: 'contacts',
    fetchPage: fetchExportPage,
    filters: { q, status: statusFilter === ALL ? undefined : statusFilter },
  });

  return (
    <AppShell
      secondaryActions={[
        // MOBIL: a sáv export-gombja el van rejtve (`hidden md:block`) — a FAB-ban viszont elérhető marad.
        // FORMÁTUM-VÁLASZTÁS NÉLKÜL, `xlsx`-re: a FAB egy koppintásos felület, egy legördülő a legördülőben
        // zsákutca lenne; az `xlsx` a leggyakoribb cél (a deszktop-gomb továbbra is mind a hármat kínálja).
        {
          key: 'export',
          icon: <ArrowDownTrayIcon />,
          label: t('common.export.label'),
          onClick: () => exportCtl.run('xlsx'),
        },
      ]}
      toolbar={
        // KÖZÖS ListToolbar: kereső (név/beosztás/e-mail/telefon/fiók) + státusz-facet + darabszám-meta.
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          filters={[
            {
              key: 'status',
              label: t('contacts.filterStatus'),
              value: statusFilter,
              onChange: (v: string) => setFilters({ status: v }),
              options: STATUS_OPTIONS.map((s) => ({
                value: s,
                label: t(`contacts.statusLabel.${s}`, { defaultValue: s }),
              })),
              // a „mind" a lista tetején; az alap `active` is aktív szűrő (Andi #95)
              allValue: 'all',
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('contacts.loading')
              : !isError && !infinite.isLoading
                ? t('contacts.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            <>
              {/* Export — jog nélkül is ott van, ahol a lista látszik („amit listázni tudsz, azt exportálhatod");
                  mobilon elrejtve, mint az „+ Új …" gomb (a szűk sávban a kereső+szűrő az elsődleges). */}
              <div className="hidden md:block">
                <ListExportButton
                  onExport={exportCtl.run}
                  pending={exportCtl.pending}
                  hasRows={!isError && rows.length > 0}
                />
              </div>
              {canManage && (
                <div className="hidden md:block">
                  <ListNewButton
                    label={t('contacts.new')}
                    onClick={() => setCreateOpen(true)}
                    enabled={canManage}
                    disabledTooltip={t('common.noCreatePermission')}
                  />
                </div>
              )}
            </>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<ContactRow>
          columns={columns}
          rows={rows}
          getRowId={(c) => c.id}
          sort="name"
          order={order}
          onSort={toggleOrder}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('contacts.noMatches')}
                description={t('contacts.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('contacts.empty')} description={t('contacts.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('contacts.actions')}
          actionsColWidth={
            // A `manage` KÉT akciót ad (szerkesztés + üdvözlő levél), a `delete` egyet.
            canManage && canDelete ? '10rem' : canManage ? '7rem' : canDelete ? '4rem' : '0'
          }
          rowActions={canManage || canDelete ? rowActions : undefined}
        />
      </div>

      {/* Create (contact=null) + Edit (contact=row) — külön instance, a munkatárs-minta szerint. */}
      <ContactFormSheet open={createOpen} onOpenChange={setCreateOpen} contact={null} />
      <ContactFormSheet
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
          }
        }}
        contact={editTarget}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('contacts.deleteTitle')}
        description={t('contacts.deleteDesc', {
          name: deleteTarget
            ? formatName(
                {
                  givenName: deleteTarget.givenName,
                  familyName: deleteTarget.familyName,
                  displayName: deleteTarget.displayName ?? deleteTarget.email,
                },
                i18n.language,
              )
            : '',
        })}
        confirmLabel={t('contacts.delete')}
        cancelLabel={t('contacts.cancel')}
        pendingLabel={t('contacts.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />

      {/* VISSZAVÉTEL — NEM destruktív (nincs `destructive`): ez felvétel-jellegű művelet. A leírás kimondja,
          mit NEM ad vissza, mert az a meglepetés forrása lenne: a személy visszajön, a hozzáférése nem. */}
      <ConfirmDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => !open && setRestoreTarget(null)}
        title={t('contacts.restoreTitle')}
        description={t('contacts.restoreDesc', {
          name: restoreTarget
            ? formatName(
                {
                  givenName: restoreTarget.givenName,
                  familyName: restoreTarget.familyName,
                  displayName: restoreTarget.displayName ?? restoreTarget.email,
                },
                i18n.language,
              )
            : '',
        })}
        confirmLabel={t('contacts.restore')}
        cancelLabel={t('contacts.cancel')}
        pendingLabel={t('contacts.restoring')}
        pending={restoreMutation.isPending}
        onConfirm={confirmRestore}
      />

      {/* Üdvözlő levél megerősítése — a szöveg KIMONDJA, hogy jelszót nem küldünk, csak beállító linket. */}
      <ConfirmDialog
        open={welcomeTarget !== null}
        onOpenChange={(open) => !open && setWelcomeTarget(null)}
        title={t('contacts.welcomeConfirmTitle')}
        description={t('contacts.welcomeConfirmDesc', {
          name: welcomeTarget
            ? formatName(
                {
                  givenName: welcomeTarget.givenName,
                  familyName: welcomeTarget.familyName,
                  displayName: welcomeTarget.displayName ?? welcomeTarget.email,
                },
                i18n.language,
              )
            : '',
        })}
        confirmLabel={t('contacts.welcomeConfirm')}
        cancelLabel={t('contacts.cancel')}
        pending={welcomeMutation.isPending}
        onConfirm={confirmWelcome}
      />
    </AppShell>
  );
}
